import { Grid, Typography } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { ChevronRight } from "@material-ui/icons";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { useQuery } from "@tanstack/react-query";
import { useHistory } from "../../shared/hooks/useHistory";
import { Collectible } from "../../collectibles/components/collectible/collectible.component";
import { getParticipantCollectibles } from "../../collectibles/services";
import { InfoTooltip } from "../../components/InfoTooltip/info-tooltip.component";
import { EmptyDetailsState } from "../../components/empty-details-state";
import { Button } from "../../shared/components/button";
import { sectionSharedCollectibles } from "./shared-collectibles.consts";
import { useSharedCollectiblesStyles } from "./shared-collectibles.styles";

const PAGE_SIZE = 4;

type Props = {
  filterBy?: string;
  hideOnEmptyFilterResult?: boolean;
};

export const SharedCollectibles = ({
  filterBy,
  hideOnEmptyFilterResult,
}: Props) => {
  const history = useHistory();
  const classes = useSharedCollectiblesStyles();

  const { data, isFetching } = useQuery({
    queryKey: ["collectibles-list", filterBy],
    queryFn: () =>
      getParticipantCollectibles({
        keyword: filterBy,
        pageNumber: 1,
        pageSize: PAGE_SIZE,
      }),
    refetchOnWindowFocus: false,
  });

  const tooltipTitle = (
    <Typography className={classes.tooltipTitle}>
      {sectionSharedCollectibles.tooltip}
    </Typography>
  );

  const handleButtonClick = () => {
    history.push("/participant/shared-collectibles");
  };

  const showCTAButtons = !isFetching && !data?.totalRecords && !filterBy;
  const showViewMoreButtons =
    (!isFetching && data?.totalRecords > PAGE_SIZE) || filterBy;

  if (hideOnEmptyFilterResult && filterBy && !data?.totalRecords) {
    return null;
  }

  return (
    <Grid container item>
      <Grid item container justifyContent="space-between">
        <Typography
          className={classes.indicatorsHeader}
          data-testid="sectionCollectible_header"
        >
          {sectionSharedCollectibles.header}
          <InfoTooltip title={tooltipTitle} placement="right">
            <InfoOutlinedIcon
              className={classes.infoIcon}
              data-testid="sectionCollectible_icon"
            />
          </InfoTooltip>
        </Typography>
        {showViewMoreButtons ? (
          <Button
            variant="outlined"
            className={classes.viewMoreBtn}
            endIcon={<ChevronRight />}
            onClick={handleButtonClick}
          >
            View more
          </Button>
        ) : null}
      </Grid>
      <Grid
        container
        justifyContent="space-between"
        className={classes.mentorContainer}
      >
        {isFetching ? (
          <CircularProgress />
        ) : !data?.totalRecords ? (
          <EmptyDetailsState
            title={sectionSharedCollectibles.title}
            imageUrl={sectionSharedCollectibles.imageUrl}
            description={sectionSharedCollectibles.description}
            action={
              showCTAButtons ? (
                <Button
                  variant="outlined"
                  className={classes.button}
                  onClick={handleButtonClick}
                >
                  {sectionSharedCollectibles.buttonText}
                </Button>
              ) : null
            }
          />
        ) : (
          data?.data.map((collectible) => (
            <Grid
              item
              key={collectible.collectableId}
              className={classes.container}
            >
              <Collectible
                id={collectible.collectableId}
                title={collectible.title}
                collectableType={collectible.collectableType}
                assignedByFullName={collectible.assignedByFullName}
                contentLink={collectible.contentLink}
              />
            </Grid>
          ))
        )}
      </Grid>
    </Grid>
  );
};
